import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Table2, Loader2, Star } from "lucide-react";

export default function FloorHistoryTable({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propId) return;
    setLoading(true);
    api.get(`/properties/${propId}/floor-history`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [propId]);

  const monthsShort = lang === "en"
    ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    : ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

  const scopeTitle = (sc, n) => {
    const base = sc === "month" ? L("stesso mese", "same month")
      : sc === "window" ? L("finestra mese ±1", "month ±1 window")
      : L("minimo globale", "global minimum");
    return `${base} · ${n} ${L("vendite", "sales")}`;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="mp-floor-history">
      <div className="flex items-center gap-2 mb-1">
        <Table2 className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Minimi storici per camera e periodo", "Historical minimums by room & period")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L(
          "Prezzo minimo NETTO realmente incassato per notte (colazione e sconti esclusi), dopo il taglio della coda bassa. È il paletto sotto cui l'AI non scende per le camere premium.",
          "Lowest NET price per night actually earned (breakfast & discounts excluded), after low-tail trimming. It's the floor the AI won't go below for premium rooms."
        )}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />{L("Carico lo storico…", "Loading history…")}
        </div>
      ) : !data || !data.rooms?.length ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="mp-floor-history-empty">
          {L("Nessuno storico prenotazioni disponibile.", "No booking history available.")}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full text-sm border-collapse" data-testid="mp-floor-history-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left py-2 pr-3 sticky left-0 bg-card z-10">{L("Camera", "Room")}</th>
                <th className="text-right py-2 px-2 whitespace-nowrap">{L("Vendite", "Sales")}</th>
                {monthsShort.map((m, i) => (
                  <th key={i} className="text-right py-2 px-2 font-semibold">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rooms.map((r, idx) => (
                <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30"
                  data-testid={`mp-floor-row-${idx}`}>
                  <td className="py-2 pr-3 sticky left-0 bg-card z-10">
                    <div className="flex items-center gap-1.5">
                      {r.is_premium && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                      <span className="font-medium truncate max-w-[220px]" title={r.name}>{r.name}</span>
                    </div>
                    {r.type && <span className="text-[10px] text-muted-foreground capitalize">{r.type.replace(/_/g, " ")}</span>}
                  </td>
                  <td className="text-right py-2 px-2 text-muted-foreground font-data">{r.samples}</td>
                  {monthsShort.map((_, i) => {
                    const mo = String(i + 1).padStart(2, "0");
                    const cell = r.months?.[mo];
                    return (
                      <td key={i} className="text-right py-2 px-2 font-data" data-testid={`mp-floor-${idx}-${mo}`}
                        title={cell ? scopeTitle(cell.scope, cell.n) : L("dati insufficienti", "insufficient data")}>
                        {cell ? (
                          <span className={cell.scope === "month" ? "text-foreground font-semibold" : "text-muted-foreground"}>
                            €{cell.min}{cell.scope !== "month" ? "*" : ""}
                          </span>
                        ) : <span className="text-muted-foreground/40">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-muted-foreground mt-3">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500 inline mr-1" />{L("camera premium", "premium room")}
            {"  ·  "}
            {L("* stima da mesi vicini o dal minimo globale (pochi dati nel mese).", "* estimate from nearby months or global minimum (few data in the month).")}
          </p>
        </div>
      )}
    </motion.div>
  );
}
